import { CommonModule } from '@angular/common';
import { Component, OnInit, Optional } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { firstValueFrom } from 'rxjs';
import { CustomButtonComponent } from 'src/app/components/custom-components/custom-button/custom-button.component';
import { CustomInputPasswordComponent } from 'src/app/components/custom-components/custom-input-password/custom-input-password.component';
import { UserService } from 'src/app/services/authentication/user.service';
import { ToastHandlerService } from 'src/app/services/toast/toast-handler.service';
import { UserUpdatePasswordModel } from './../../../../models/authentication/user-update-password.model';
import { CustomValidators } from './../../../../validators/custom-validators';

@Component({
  selector: 'app-update-password',
  templateUrl: './update-password.component.html',
  imports: [CommonModule, ReactiveFormsModule, CustomInputPasswordComponent, CustomButtonComponent],
  styleUrls: ['./update-password.component.scss'],
  standalone: true
})

export class UpdatePasswordComponent implements OnInit {
  formUpdatePassword: FormGroup = new FormGroup({});

  constructor(public config: DynamicDialogConfig,
    private formBuilder: FormBuilder,
    @Optional() private ref: DynamicDialogRef,
    private router: Router,
    private toastHandlerService: ToastHandlerService,
    private userService: UserService) { }

  ngOnInit(): void {
    this.validateForm();
  }

  async send() {
    Object.keys(this.formUpdatePassword.controls).forEach(key => {
      const control = this.formUpdatePassword.get(key);
      if (control && control.invalid) {
        console.warn(`${key} es inválido`, control.errors, 'value:', control.value);
      }
    });

    if (this.formUpdatePassword.valid) {
      const modelUser = this.formUpdatePassword.value as UserUpdatePasswordModel;
      if (modelUser.currentPasswordHash == modelUser.passwordHash) {
        this.toastHandlerService.showToast('warning', `La nueva contraseña no puede ser igual a la contraseña actual.`);
        return
      }
      try {
        await firstValueFrom(this.userService.UpdateUserAuthenticatedPassword(modelUser));
        this.toastHandlerService.showToast('success', `La contraseña fue modificada con éxito. Inicie sesión nuevamente.`);
        this.ref.close();
        this.signOff();
      } catch (error) {
        console.error('Error al actualizar la contraseña.', error);
      }
    } else {
      this.showValidationMessages();
    }
  }

  showValidationMessages() {
    const controlMessages: Record<string, Record<string, string>> = {
      currentPasswordHash: {
        required: 'La  contraseña actual es obligatoria.',
      },
      passwordHash: {
        required: 'La contraseña es obligatoria.',
        minlength: 'La contraseña debe tener mínimo 8 caracteres.',
        hasNumber: 'La contraseña debe incluir al menos un número.',
        hasCapitalCase: 'La contraseña debe incluir al menos una letra mayúscula.',
        hasSmallCase: 'La contraseña debe incluir al menos una letra minúscula.',
        hasSpecialCharacters: 'La contraseña debe incluir al menos un carácter especial.',
      },
      confirmPasswordHash: {
        required: 'La confirmación de contraseña es obligatoria.',
        NoPassswordMatch: 'Las contraseñas no coinciden.'
      }
    };

    Object.keys(controlMessages).forEach(controlName => {
      const control = this.formUpdatePassword.get(controlName);
      if (control && control.invalid && control.errors) {
        const errors = control.errors;
        Object.keys(errors).forEach(errorKey => {
          const message = controlMessages[controlName][errorKey];
          if (message) {
            this.toastHandlerService.showToast('warning', message, 'Atención');
          }
        });
      }
    });
  }

  validateForm() {
    this.formUpdatePassword = this.formBuilder.group(
      {
        currentPasswordHash: ['', [Validators.required]],
        passwordHash: [null,
          Validators.compose([
            Validators.required,
            // verificar número
            CustomValidators.patternValidator(/\d/, {
              hasNumber: true
            }),
            // verificar letras mayúsculas
            CustomValidators.patternValidator(/[A-Z]/, {
              hasCapitalCase: true
            }),
            // verificar letra minúscula
            CustomValidators.patternValidator(/[a-z]/, {
              hasSmallCase: true
            }),
            // verificar carácter especial
            CustomValidators.patternValidator(
              /[ !@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/,
              {
                hasSpecialCharacters: true
              }
            ),
            Validators.minLength(8)
          ])
        ],
        confirmPasswordHash: [null, Validators.compose([Validators.required])]
      },
      {
        // verificar confirmar contraseña coinciden
        validator: CustomValidators.passwordMatchValidator
      }
    );
  }

  signOff() {
    this.router.navigate([`/login`]);
  }
}
